import React, { useState, useEffect, useMemo } from "react";

import PriceDetailsModal from "../modals/PriceDetailsModal";
import ViewDetailsTable from "../shared/table/ViewDetailsTable";

import { CollapseHeadingIcon, ExpandHeadingIcon } from "../../assets";
import { SAVE_QUOTATION } from "../../apis/end-points";
import { postApi } from "../../apis/api";

interface ViewDetailsRowDataType {
  serial: number;
  serviceName?: string;
  description: string;
  serviceLevel: string;
  quotePrice: string;
  agentName: string;
  basePrice: string;
  fuelSurcharge: string;
}

interface GetQuotePropsType {
  setFields: (field: any) => void;
  quoteData: any;
  setIsQuote: (value: boolean) => void;
  /**
   * Receives a single quote id from the LTL + Final Mile caller, or the full list of
   * quote ids when Final Mile saves every agent and service as one group.
   */
  saveQuotation?: (quoteId: string | string[]) => void;
  quoteSelectedId?: string;
  formData?: any;
  calledFrom?: any;
  masterHubName?: string;
  masterHubAddress?: any;
  masterHubNameToTransfer?: string;
  masterHubAddressToTransfer?: any;
  lfmQuoteId?: any;
  setIsSelectedParent?: (data: any) => void;
}

// Define the columns to be used in the ViewDetailsTable
const columns = [
  { header: "S.", key: "serial", className: "count", count: true },
  {
    header: "Service Name",
    key: "serviceName",
    className: "dark",
    sortable: true,
  },
  { header: "Description", key: "description", className: "dark" },
  { header: "Quote Price", key: "quotePrice", className: "dark", dollar: true },
];

interface Address {
  address?: string;
  aptSuit?: string;
  zipCode?: string;
  city?: string;
  state?: string;
  country?: string;
  contactPerson?: string;
  phoneNumber?: string;
  countryCallingCode?: string;
  contactEmail?: string[];
}

interface SelectedData {
  hubName?: string;
  agentName?: string;
  address?: Address;
  operationHrsFrom?: string;
  operationHrsTo?: string;
  addressName?: string;
  distanceToDestination?: number;
  serviceName?: string;
  quotePrice?: string;
  id?: string;
  agentHubId?: string;
  allQuoteId?: string;
  quoteId?: string;
}

/**
 * GetQuote Component
 *
 * This component displays a list of quotes and allows the user to view details of a selected quote,
 * as well as to select a quote, which populates form fields with relevant data.
 *
 * @param setFields - Function to update form fields with selected quote data
 * @param quoteData - The array of quotes to display
 * @param setIsQuote - Function to update the state indicating whether a quote has been selected
 */
const GetQuote: React.FC<GetQuotePropsType> = ({
  setFields,
  quoteData,
  setIsQuote,
  saveQuotation,
  quoteSelectedId,
  formData,
  calledFrom,
  masterHubName,
  masterHubAddress,
  masterHubNameToTransfer,
  masterHubAddressToTransfer,
  lfmQuoteId,
  setIsSelectedParent

}) => {
  
  const [viewPriceDetailsModal, setViewPriceDetailsModal] =
    useState<boolean>(false);
  const [viewDetailRowData, setViewDetailRowData] =
    useState<ViewDetailsRowDataType>();
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<SelectedData | null>(null);
  // Every agent section starts open, so the whole quote list reads at a glance instead
  // of one agent at a time. Tracking the *collapsed* sections rather than the single
  // open one keeps "all expanded" as the default and lets any number stay open.
  const [collapsedSections, setCollapsedSections] = useState<{
    [index: number]: boolean;
  }>({});

  /**
   * Every quote id on screen, across all agents and all of their services. Saving
   * stores the whole result set as one group, so this is a single action rather than
   * one per agent.
   */
  const allQuoteIds = useMemo(
    () =>
      (quoteData ?? []).reduce((ids: string[], hub: any) => {
        (hub?.services ?? []).forEach((service: any) => {
          if (service?.quoteId) {
            ids.push(service.quoteId);
          }
        });
        return ids;
      }, []),
    [quoteData]
  );

  /**
   * Handles the action of viewing details of a specific row in the table.
   * @param rowData - The data of the row to view in detail.
   */
  const handleViewDetails = (rowData: any) => {
    setViewDetailRowData(rowData);
    setViewPriceDetailsModal(true);
  };

  /**
   * Handles the action of selecting a specific quote.
   * Populates form fields with the data from the selected quote.
   * @param rowData - The data of the selected quote.
   */
  const handleSelect = (rowData: any) => {

    setSelectedData(rowData);
    const data = rowData?.address;
    setIsSelected(true);
    setIsSelectedParent && setIsSelectedParent(true);
    setFields((prev: any) => ({
      ...prev,
      MidStopAddress: data?.address || "",
      MidStopAPTSuit: data?.aptSuit || "",
      MidStopZipcode: data?.zipCode || "",
      MidStopCity: data?.city || "",
      MidStopState: data?.state || "",
      MidStopCountry: data?.country || "",
      MidStopContactName: data?.contactPerson || "",
      MidStopPhoneNumber: data?.phoneNumber || "",
      MidStopPhoneNumberExt: data?.countryCallingCode || "+1",
      MidStopContactEmail: data?.contactEmail || [],
      agentHubId: rowData?.agentHubId,
      selectedServiceId: rowData?.id,
      selectedQuoteId: rowData?.quoteId,
      allQuoteId: rowData?.allQuoteId,
    }));
    formData = {
      ...formData,
      MidStopAddress: data?.address || "",
      MidStopAPTSuit: data?.aptSuit || "",
      MidStopZipcode: data?.zipCode || "",
      MidStopCity: data?.city || "",
      MidStopState: data?.state || "",
      MidStopCountry: data?.country || "",
      MidStopContactName: data?.contactPerson || "",
      MidStopPhoneNumber: data?.phoneNumber || "",
      MidStopPhoneNumberExt: data?.countryCallingCode || "+1",
      MidStopContactEmail: data?.contactEmail || [],
      agentHubId: rowData?.agentHubId,
      selectedServiceId: rowData?.id,
      selectedQuoteId: rowData?.quoteId,
      allQuoteId: rowData?.allQuoteId,
    }
    setIsQuote(true);
  };

  /**
   * Toggles the expanded state of a section, allowing the user to view or hide details.
   * @param index - The index of the section to toggle.
   */
  const toggleSection = (index: number) => {
    setCollapsedSections((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  /**
   * A fresh quote re-opens everything. The keys are positions in the previous result
   * set, so carrying them over would collapse whichever agents happen to land on the
   * same indices.
   */
  useEffect(() => {
    setCollapsedSections((prev) => (Object.keys(prev).length ? {} : prev));
  }, [quoteData]);

  useEffect(() => {
    if (quoteSelectedId) {
      const selectedQuoteData = quoteData[0]?.services?.filter((item: any) => item?.id === quoteSelectedId)?.[0] ?? {};
      handleSelect({ ...selectedQuoteData, ...quoteData[0]?.info, id: selectedQuoteData?.serviceId })
    }
  }, [quoteSelectedId])

  return (
    <>
      {/* Mounted only once a quote is picked. The Get Quote button's row above is
          `mb_0` and `.formRow` carries margin-bottom only, so this box needs a top gap
          of its own to sit clear of the button. Keeping the whole row out of the tree
          while nothing is selected also drops an empty `.formRow`'s margin from above
          the agent list. 15px matches the sibling getQuoteBox on the order screen. */}
      {isSelected && (
        <div className="formRow" style={{ marginTop: "15px" }}>
          <div className="getQuoteBox">
            <p className="para">
              {/* Hub: Hub Name C/O Agent Name, City, State, Zipcode, Country, Hours of operation, Mile */}
              {`Hub: ${masterHubName
                ? masterHubName
                : selectedData?.hubName
                  ? selectedData.hubName
                  : ""
                } C/O ${selectedData?.agentName || ""}, ${masterHubAddress?.city
                  ? masterHubAddress.city
                  : selectedData?.address?.city
                    ? selectedData.address.city
                    : ""
                }, ${masterHubAddress?.state
                  ? masterHubAddress.state
                  : selectedData?.address?.state
                    ? selectedData.address.state
                    : ""
                }, ${masterHubAddress?.zipCode
                  ? masterHubAddress.zipCode
                  : selectedData?.address?.zipCode
                    ? selectedData.address.zipCode
                    : ""
                }, ${masterHubAddress?.country
                  ? masterHubAddress.country
                  : selectedData?.address?.country
                    ? selectedData.address.country
                    : ""
                } | Hrs of Operation: ${masterHubAddress?.operationHrsFrom
                  ? masterHubAddress.operationHrsFrom + " - " + (masterHubAddress?.operationHrsTo || "")
                  : selectedData?.operationHrsFrom
                    ? selectedData.operationHrsFrom + " - " + (selectedData?.operationHrsTo || "")
                    : ""
                } | ${selectedData?.distanceToDestination} mi`}
            </p>
            <h3 className="title title--h3">
              Quote Price: $
              {selectedData?.quotePrice
                ? parseFloat(selectedData?.quotePrice).toFixed(2)
                : 0.0}
            </h3>
          </div>
        </div>
      )}
      {/* One action for the whole result set: saving stores every agent and every
          service under a single quote group. Hidden on saved-quote replay, matching
          the previous per-agent button. */}
      {!isSelected && !quoteSelectedId && saveQuotation && allQuoteIds.length > 0 && (
        // `mb_0` because this row only introduces the agent list right below it —
        // `.formRow`'s default 12px would read as a gap between the button and a
        // block it belongs to, the same reason the agent rows below carry it.
        <div className="formRow formRow--gapNone mb_0">
          <div className="btn__group btn__group--end mb_0">
            <button
              className="btn btn__white"
              type="button"
              onClick={() => saveQuotation(allQuoteIds)}
            >
              Save Quote
            </button>
          </div>
        </div>
      )}
      {!isSelected &&
        quoteData?.map((item: any, index: number) => {
          const info = item?.info;
          const isExpanded = !collapsedSections[index];
          // Each agent carries its own master hub. Fall back to the component-level
          // props so the LTL + Final Mile and saved-quote callers are unaffected.
          // An agent mapped without a master hub must not fall back: the props hold
          // only the last master hub in the result set, so it would be shown under
          // that hub's name and address instead of its own.
          const ownHubOnly = info?.hasMasterHub === false;
          const hubMasterName = ownHubOnly
            ? undefined
            : info?.masterHubName ?? masterHubName;
          const hubMasterAddress = ownHubOnly
            ? undefined
            : info?.masterHubAddress ?? masterHubAddress;
          return (
            <div
              className="formRow formRow--gapNone mb_0"
              key={info?.agentHubId ?? item?.services?.[0]?.agentHubId ?? `quote-${index}`}
              // `mb_0` is `margin-bottom: 0 !important`, so each hub block is flush with
              // the next and the following Hub heading reads as part of the table above
              // it. The gap goes on top of every block but the first, which has to stay
              // tight under the Save Quote button. 15px matches the getQuoteBox above.
              style={index > 0 ? { marginTop: "15px" } : undefined}
            >
              <div className="form__section__title form__section__title--flex">
                <h1
                  className="form__section__title form__section__title--second mb_0"
                  onClick={() => toggleSection(index)}
                >
                  {isExpanded ? <CollapseHeadingIcon /> : <ExpandHeadingIcon />}
                  {`Hub: ${hubMasterName ? hubMasterName : info?.hubName ? info?.hubName : ''} C/O ${info?.agentName || ""},
                  ${hubMasterAddress?.city
                      ? hubMasterAddress.city
                      : info?.address?.city
                        ? info?.address?.city
                        : ""
                    }
                    ,
                    ${hubMasterAddress?.state
                      ? hubMasterAddress.state
                      : info?.address?.state
                        ? info?.address?.state
                        : ""
                    }, ${hubMasterAddress?.zipCode
                      ? hubMasterAddress.zipCode
                      : info?.address?.zipCode
                        ? info?.address?.zipCode
                        : ""
                    }, ${hubMasterAddress?.country
                      ? hubMasterAddress.country
                      : info?.address?.country
                        ? info?.address?.country
                        : ""
                    }
                    | Hrs of Operation: ${hubMasterAddress?.operationHrsFrom
                      ? hubMasterAddress.operationHrsFrom +
                      " - " +
                      (hubMasterAddress?.operationHrsTo || "")
                      : info?.operationHrsFrom
                        ? info?.operationHrsFrom + " - " + (info?.operationHrsTo || "")
                        : ""
                    }
                    | ${parseFloat(info?.distanceToDestination).toFixed(2)} mi`}
                  {/*
                {`${info?.agentName || ""} | Hrs of
              Operation: ${info?.operationHrsFrom || ""} - ${
                  info?.operationHrsTo || ""
                } | Hub: ${info?.hubName || ""} - ${info?.addressName || ""}, ${
                  info?.address?.city || ""
                }, ${info?.address?.state || ""}, ${
                  info?.address?.zipCode || ""
                }, ${
                  info?.address?.country || ""
                } | ${info?.distanceToDestination?.toFixed(2)} mi`}
                */}
                </h1>
              </div>
              {isExpanded && (
                <div className="tableWrap tableWrap--order p_0">
                  <ViewDetailsTable
                    columns={columns}
                    data={item?.services}
                    handleViewDetails={handleViewDetails}
                    handleSelect={handleSelect}
                    quoteSelectedId={quoteSelectedId}
                    formData={formData}
                    callingFrom={calledFrom}
                    isSelected={isSelected}
                    setIsSelected={setIsSelected}
                    setIsSelectedParent={setIsSelectedParent}
                    masterHubNameToTransfer={masterHubNameToTransfer}
                    masterHubAddressToTransfer={masterHubAddressToTransfer}
                    lfmQuoteId={lfmQuoteId}
                  />
                </div>
              )}
            </div>
          );
        })}
      {viewPriceDetailsModal && (
        <PriceDetailsModal
          setConfirmModal={setViewPriceDetailsModal}
          rowData={viewDetailRowData}
        />
      )}
    </>
  );
};

export default GetQuote;
